// Work done on this phone that hasn't sent yet, laid over the day it was done on (docs/plans/offline/PLAN-v2.md §4,
// 2026-10-01). Pure, so it is tested on its own (scripts/offline.test.mjs); Today (screens/today.ts) uses it.
// Every Today change to a job is a PATCH /api/cleans/<id> in the queue (api.ts), and the queue survives the phone
// closing Roundly, so laying those changes over the saved copy, oldest first, shows the day as the worker left it.
// Refused changes (conflict, failed) are NOT laid on: the owner changed the job, and the card says so instead.
import { clockWords, currencySymbol } from './round-config'

export const CLEAN_PATH = /^\/api\/cleans\/(\d+)$/
const CUSTOMER_PATH = /^\/api\/customers\/(\d+)$/
const TIME_FIELDS = new Set(['started_at', 'finished_at', 'outsides_done_at'])

interface QueuedLike {
  method: string
  path: string
  body?: unknown
  status: string
  createdAt: number
}

export function layWaiting<T extends { id: number }>(cleans: T[], queue: readonly QueuedLike[]): T[] {
  const entries = queue
    .filter((q) => q.method === 'PATCH' && CLEAN_PATH.test(q.path) && q.status !== 'conflict' && q.status !== 'failed')
    .slice()
    .sort((a, b) => a.createdAt - b.createdAt)
  // A Warning or Just a note chosen on a customer's page offline shows on their visits straight away (0088, GPT-6.1's
  // notes review 4): merged note by note, as the server does.
  const kindsWaiting = queue
    .filter((q) => q.method === 'PATCH' && CUSTOMER_PATH.test(q.path) && q.status !== 'conflict' && q.status !== 'failed' && !!q.body && (q.body as Record<string, unknown>).note_kinds != null)
    .slice()
    .sort((a, b) => a.createdAt - b.createdAt)
  if (entries.length === 0 && kindsWaiting.length === 0) return cleans
  const out = cleans.map((c) => ({ ...c }))
  for (const q of kindsWaiting) {
    const customer = Number(CUSTOMER_PATH.exec(q.path)![1])
    let change: Record<string, unknown> = {}
    const sent = (q.body as Record<string, unknown>).note_kinds
    try { change = (typeof sent === 'string' ? JSON.parse(sent) : sent) as Record<string, unknown> ?? {} } catch { continue }
    if (!change || typeof change !== 'object') continue
    for (const c of out) {
      const r = c as unknown as Record<string, unknown>
      if (Number(r.customer_id) !== customer) continue
      let had: Record<string, unknown> = {}
      try { had = JSON.parse(String(r.note_kinds ?? '{}')) ?? {} } catch { had = {} }
      for (const [k, v] of Object.entries(change)) { if (v === null) delete had[k]; else had[k] = v }
      r.note_kinds = Object.keys(had).length ? JSON.stringify(had) : null
    }
  }
  const byId = new Map(out.map((c) => [c.id, c]))
  for (const q of entries) {
    const row = byId.get(Number(CLEAN_PATH.exec(q.path)![1]))
    if (!row || !q.body || typeof q.body !== 'object') continue
    const r = row as unknown as Record<string, unknown>
    for (const [k, v] of Object.entries(q.body as Record<string, unknown>)) {
      if (k === 'version') continue
      if (k === 'note_append') {
        if (typeof v === 'string' && v) r.notes = r.notes ? `${String(r.notes)}\n${v}` : v
        continue
      }
      // Times go to the server as ISO; the day shows them as stored ("2026-10-02 09:14:00").
      r[k] = TIME_FIELDS.has(k) && typeof v === 'string' ? v.slice(0, 19).replace('T', ' ') : v
    }
    // A time sent without how it was got has none, as the server stores it (0083, cleans.ts applyJobTimeRules).
    const b = q.body as Record<string, unknown>
    if ('started_at' in b && !('started_how' in b)) r.started_how = null
    if ('finished_at' in b && !('finished_how' in b)) r.finished_how = null
  }
  return out
}

// What a refused change recorded, in the worker's words, so it can be passed on before it is let go (Astra, 1 Oct:
// "show recorded values before explicit discard"). Money as pounds; times as the phone's clock.
const BLOCKED: Record<string, string> = { not_in: 'Not in', no_access: 'No access', weather: 'Weather', customer_cancelled: 'Customer cancelled', other: 'Other' }

function clockOf(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  const mm = String(d.getMinutes()).padStart(2, '0')
  return clockWords(d.getHours(), mm)
}

export function describeChange(body: unknown): string[] {
  if (!body || typeof body !== 'object') return []
  const b = body as Record<string, unknown>
  const out: string[] = []
  if (typeof b.started_at === 'string') out.push(`Started ${clockOf(b.started_at)}`)
  if ('started_at' in b && b.started_at === null) out.push('Start taken off')
  if (typeof b.finished_at === 'string') out.push(`Finished ${clockOf(b.finished_at)}`)
  if ('finished_at' in b && b.finished_at === null) out.push('Finish taken off')
  if (b.state === 'done') out.push('Done')
  if (b.payment_status === 'paid') out.push(b.payment_method === 'cash' ? 'Paid cash' : b.payment_method === 'bank' ? 'Paid by bank' : 'Paid')
  if (b.payment_status === 'unpaid') out.push('Not paid yet')
  if (typeof b.amount_pence === 'number') out.push(`${currencySymbol()}${(b.amount_pence / 100).toFixed(2).replace(/\.00$/, '')}`)
  if (typeof b.blocked_reason === 'string') out.push(`Couldn't do it: ${BLOCKED[b.blocked_reason] ?? b.blocked_reason}`)
  if (typeof b.blocked_note === 'string' && b.blocked_note) out.push(`"${b.blocked_note}"`)
  if (typeof b.note_append === 'string' && b.note_append) out.push(`Note: ${b.note_append}`)
  if (typeof b.notes === 'string' && b.notes) out.push(`Note: ${b.notes}`)
  return out
}
